import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { FaroButtonContained } from "@components/common/faro-button-contained";
import { AutoCompleteMessage } from "@components/common/faro-text-field/faro-text-field-message";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import {
  AutoCompleteMemberOption,
  AutoCompleteTeamOption,
} from "@components/common/members-autocomplete/members-autocomplete-types";
import {
  createMemberOption,
  createTeamOption,
} from "@components/common/members-autocomplete/members-autocomplete-utils";
import { EnableGroupCreationCheckbox } from "@components/groups/enable-group-creation-checkbox";
import { SelectGroupRole } from "@components/role/select-group-role";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { UserId } from "@custom-types/base-types";
import { RoleDisplayNames } from "@custom-types/member-types";
import { Dialog } from "@flat-ui";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { useTeamUtils } from "@hooks/use-team-utils";
import { Stack } from "@mui/material";
import { useAppParams } from "@router/router-helper";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { EmailUtils } from "@stellar/utils";
import { selectedGroupMembersSelector } from "@store/groups/groups-selector";
import {
  addProcessingGroups,
  removeProcessingGroups,
  setOne,
} from "@store/groups/groups-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { teamsSelector } from "@store/teams/teams-selector";
import { delay } from "@utils/time-utils";
import { GroupMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { createGroupsPermission } from "@utils/user-utils";
import { useCallback, useMemo, useState } from "react";

interface Props {
  /** The selected group */
  group: FaroCoreApiTypes.GroupDetailResponse;
}

/** Renders the invite member button and the functionality in member page */
export function InviteMemberToGroup({ group }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();
  const { groupId, companyId } = useAppParams();
  const { respondToUser } = useRespondToInvite();
  const { isTeamName, getTeamIdByName } = useTeamUtils();
  const { trackEvent } = useTrackEvent();

  const companyMembers = useCompanyMembers();
  const teams = useAppSelector(teamsSelector);
  const groupMembers = useAppSelector(selectedGroupMembersSelector);

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [shouldCreateGroups, setShouldCreateGroups] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] =
    useState<FaroCoreApiTypes.GroupRole>("PROJECT_MANAGER");
  const [message, setMessage] = useState<AutoCompleteMessage | undefined>();

  /** Flag whether the input field in the autocomplete component has a valid email */
  const [isMemberInputValid, setIsMemberInputValid] = useState<boolean>(false);

  // Disable the `Send Invite` button if no user has been selected and the input is not a valid email.
  const shouldSendInviteDisable =
    !selectedMembers.length && !isMemberInputValid;

  /** Reformation company members data for faro autocomplete component */
  const memberOptions: AutoCompleteMemberOption[] = useMemo(
    () =>
      companyMembers.map((member) => {
        const alreadyMemberInGroup = group.members.find(
          (groupMember) => member.identity === groupMember.identity
        );
        const isAlreadyMemberInGroup = !!alreadyMemberInGroup;

        return createMemberOption({
          member,
          isDisabled: isAlreadyMemberInGroup,
          disabledMessage: isAlreadyMemberInGroup
            ? `Already in group ${
                alreadyMemberInGroup?.role
                  ? `as ${RoleDisplayNames[alreadyMemberInGroup?.role]}`
                  : ""
              }`
            : "",
        });
      }),
    [companyMembers, group.members]
  );

  const checkIfTeamIsAlreadyInProject = useCallback(
    (team: FaroCoreApiTypes.Team) =>
      groupMembers.find((groupMember) => team.id === groupMember.identity),
    [groupMembers]
  );

  function createDisabledMessage(
    isAlreadyTeamInProject: boolean,
    role: FaroCoreApiTypes.CompanyRole | undefined
  ): string {
    if (!isAlreadyTeamInProject) {
      return "";
    }

    return `Already in group ${role ? `as ${RoleDisplayNames[role]}` : ""}`;
  }

  const teamOptions: AutoCompleteTeamOption[] = useMemo(
    () =>
      teams.map((team) => {
        const alreadyTeamInProject = checkIfTeamIsAlreadyInProject(team);

        const isAlreadyTeamInProject = !!alreadyTeamInProject;

        return createTeamOption({
          team,
          isDisabled: isAlreadyTeamInProject,
          disabledMessage: createDisabledMessage(
            isAlreadyTeamInProject,
            alreadyTeamInProject?.role
          ),
        });
      }),
    [checkIfTeamIsAlreadyInProject, teams]
  );

  function handleMemberSelect(members: UserId[]): void {
    const isMembersContainsEmail = members.some((member) =>
      EmailUtils.isValidEmail(member)
    );

    /** Show hints about adding or selecting an email */
    if (isMembersContainsEmail) {
      setMessage({
        type: "info",
        helperText:
          "Invitation to this group will be sent to the new email addresses",
      });
    } else {
      setMessage(undefined);
    }
    setSelectedMembers(members);
  }

  /** Return true if the new email is valid and it doesn't exist in the group */
  function isNewEmailValid(email: string): boolean {
    return (
      EmailUtils.isValidEmail(email) && !isAlreadyMemberInCurrentGroup(email)
    );
  }

  /** Return true if member already exist with same email in the group */
  function isAlreadyMemberInCurrentGroup(email: string): boolean {
    return group.members.some(
      (member) => member.email === email || member.identity === email
    );
  }

  /**
   * Function to be called when the input value changes
   * It sets the valid emails to the addedEmails state and removes them from the input value
   */
  function onInputChange(value: string): void {
    const isValueExistOnMemberList = memberOptions.some(({ label }) =>
      label.includes(value)
    );

    if (!value) {
      // Removing message on clearing the search text
      setMessage(undefined);
      setIsMemberInputValid(false);
    } else if (!isValueExistOnMemberList && !EmailUtils.isValidEmail(value)) {
      // Showing error message for new and invalid email
      setMessage({ type: "error", helperText: "Enter a valid email" });
      setIsMemberInputValid(false);
    } else if (isAlreadyMemberInCurrentGroup(value)) {
      // Showing info message if member already exist in the group
      setMessage({
        type: "info",
        helperText: "A member with the same email already exists in the group",
      });
      setIsMemberInputValid(false);
    } else {
      if (EmailUtils.isValidEmail(value)) {
        setIsMemberInputValid(true);
      }
      setMessage(undefined);
    }
  }

  /** Close the dialog and reset loading state */
  async function closeDialog(): Promise<void> {
    setIsDialogOpen(false);
    // Delay to ensure dialog closes before resetting loading state
    // This prevents duplicate invitation requests
    await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
    setIsLoading(false);
  }

  /** Submit members to be invited */
  async function handleConfirm(): Promise<void> {
    setIsLoading(true);

    trackEvent({
      name: GroupMemberEvents.inviteMembers,
      props: {
        numberOfMembers: selectedMembers.length,
        role: selectedRole,
      },
    });

    try {
      await createPayloadAndSendInvite();

      void closeDialog();
    } catch (error) {
      setIsLoading(false);

      handleErrorWithToast({
        id: `inviteMember-${Date.now().toString()}`,
        title: "Cannot invite member(s)",
        error,
      });
    }
  }

  /** This function creates payload and call the appropriate route */
  async function createPayloadAndSendInvite(): Promise<void> {
    // Additional permission to be added if user should be able to create groups
    const permission: FaroCoreApiTypes.UserPermission | undefined =
      shouldCreateGroups && selectedRole === "COMPANY_MANAGER"
        ? createGroupsPermission
        : undefined;

    // Replacing team names by team ids to include the identity in payload
    const identitiesWithTeam = selectedMembers.map((identity) => {
      return getTeamIdByName(identity) ?? identity;
    });

    const payload: FaroCoreApiTypes.BulkMemberInvitePayload<"group"> = {
      assignments: [
        {
          identities: identitiesWithTeam,
          permission,
          role: selectedRole,
        },
      ],
    };

    if (companyId && groupId && selectedMembers.length) {
      // Add the groupId to processing groups to start the loading spinner in the group
      dispatch(addProcessingGroups([groupId]));

      const response: FaroCoreApiTypes.AddMembersToGroupResponse =
        await faroCoreApiClient.groups.addMembersToGroup({
          companyId,
          groupId,
          payload,
        });

      // Add invited member to the store. response.data.members exist when the response is either success or warning
      if (response.data.members) {
        const groupWithUpdatedMembers = {
          ...group,
          members: response.data.members,
        };

        dispatch(setOne(groupWithUpdatedMembers));
      }

      // Remove groupId from processing group to stop the loading spinner in the group
      dispatch(removeProcessingGroups([groupId]));

      respondToUser(response, selectedMembers.length);
    }
  }

  /** Default setup when invite dialog open */
  function onOpenInviteDialog(): void {
    setMessage(undefined);
    setSelectedMembers([]);
    setIsDialogOpen(true);
  }

  return (
    <>
      <FaroButtonContained onClick={onOpenInviteDialog}>
        Invite members
      </FaroButtonContained>
      <Dialog
        title="Invite members to group"
        confirmText="Send Invite"
        open={isDialogOpen}
        onConfirm={handleConfirm}
        showSpinner={isLoading}
        isConfirmDisabled={shouldSendInviteDisable}
        onCancel={() => setIsDialogOpen(false)}
      >
        <Stack marginBottom={SPACE_ELEMENTS_OF_MODAL}>
          <SelectGroupRole
            selectedRole={selectedRole}
            onChange={setSelectedRole}
            isTableCell={false}
          />
        </Stack>
        <Stack>
          <MembersAutocomplete
            options={[...teamOptions, ...memberOptions]}
            handleChange={handleMemberSelect}
            onInputChange={onInputChange}
            validateNewOption={isNewEmailValid}
            message={message}
            labelTitle="Members"
            hasAutoFocus={true}
            isTeamName={isTeamName}
            placeHolder={`Email(s), ${TEAM_DISPLAY_NAME}(s) separated by comma`}
          />
        </Stack>

        <EnableGroupCreationCheckbox
          selectedRole={selectedRole}
          shouldCreateGroups={shouldCreateGroups}
          membersCount={selectedMembers.length}
          setShouldCreateGroups={setShouldCreateGroups}
        />
      </Dialog>
    </>
  );
}
